import { ComponentRef, createNgModuleRef, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { ControlState } from '../api/model/models';
import { CfControlComponent } from './cf-control.component';
import { CplaceControlComponent, CplaceControlService } from './components';
import { CfControlPlugin, CfControlPluginService } from './plugins';

type ControlRegistry = Map<string, Map<string, CfControlComponent>>;

@Injectable()
export class CfControlService {
  private registeredControlsToBeLoaded: ControlRegistry = new Map<string, Map<string, CfControlComponent>>();

  constructor(
    private pluginService: CfControlPluginService,
    private injector: Injector,
    private cplaceControlService: CplaceControlService
  ) {}

  /**
   * Renders the control in the host component
   * @param controlState Control state
   * @param host Host component reference
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public render<T extends CplaceControlComponent<any>>(controlState: ControlState, host: ViewContainerRef): void {
    const plugin: CfControlPlugin<T> | undefined = this.pluginService.initPluginByType(controlState.controlParameters.componentName);
    if (plugin) {
      const componentRef: ComponentRef<T> = host.createComponent<T>(plugin.component, {
        ngModuleRef: createNgModuleRef(plugin.module, this.injector),
        injector: this.injector,
      });
      componentRef.instance.initializeControlState(controlState);
    }
  }

  /**
   * Registers a control to be loaded.
   * @param component Control component instance.
   * @param uid Entity UID.
   * @param feature Feature name.
   */
  public registerForLoading(component: CfControlComponent, uid: string, feature: string) {
    if (this.registeredControlsToBeLoaded.has(uid)) {
      this.registeredControlsToBeLoaded.get(uid)?.set(feature, component);
    } else {
      const mapping = new Map<string, CfControlComponent>();
      mapping.set(feature, component);
      this.registeredControlsToBeLoaded.set(uid, mapping);
    }

    this.batchLoadControls();
  }

  /**
   * Gets the number of registered controls in the DOM.
   */
  public getNumRegisteredControlsInDom(): number {
    return document.querySelectorAll('cf-frontend-sdk-control[registered-for-loading="true"]').length;
  }

  /**
   * Loads controls in batches. All controls registered in the same Angular lifecycle will be loaded at once.
   * @private
   */
  private batchLoadControls() {
    const registeredControlsInDom = this.getNumRegisteredControlsInDom();
    const registeredControls = Array.from(this.registeredControlsToBeLoaded.values())
      .map((components) => components.size)
      .reduce((acc, size) => acc + size, 0);

    if (registeredControlsInDom !== registeredControls) {
      return;
    }

    const controlsToBeLoaded = new Map<string, Map<string, CfControlComponent>>();

    for (const [uid, components] of this.registeredControlsToBeLoaded) {
      controlsToBeLoaded.set(uid, components);
    }

    this.registeredControlsToBeLoaded.clear();

    for (const [uid, components] of controlsToBeLoaded.entries()) {
      for (const component of components.values()) {
        component.registeredForLoading = false;
      }

      this.cplaceControlService.get(uid, Array.from(components.keys())).subscribe(
        (response) => {
          response.featureResponses.forEach((featureResponse) => {
            const component = components.get(featureResponse.featureName);

            if (component) {
              component.controlState = featureResponse.controlState;
            }

            if (featureResponse.errorMessage) {
              console.error(
                `(${this.constructor.name}) [entity=${uid}, feature=${featureResponse.featureName}] Error getting control state:`,
                featureResponse.errorMessage
              );
            }
          });
        },
        (error) => {
          console.error(`(${this.constructor.name}) [entity=${uid}] Error getting control states for requested features:`, error);
        }
      );
    }
  }
}
